import { Alert } from "@hcsc-fiber/react";
import axios from "axios";
import classNames from "classnames";
import React, { useContext, useEffect, useState } from "react";
import { consentsUrl, scopesDescriptionURL } from "../constants/urls";
import { StateContext } from "../context/stateContext";
import { getAccessToken } from "../endpoints/acessToken";
import { useFetch } from "../hooks/useFetch";
import styles from "../styles/third-party.module.scss";
import { translations } from "../translate";
import { getFirstWordInString } from "../utils/getFirstWordInString";
import { getStateName } from "../utils/helperFunction";
import ThirdPartyAccordion from "./accordian1";
import DisconnectButton from "./DisconnectButton";
import { ErrorLayout } from "./ErrorLayout/ErrorLayout";
import { ScopeHtmlContent } from "./ThirdPartyPanel";
type AppNameAndScopes = {
  appName: string;
  scopes: string[];
};

export const parseAppNameAndScopes = (sentence = ""): AppNameAndScopes => {
  const scopes = sentence.split(" ").slice(1);
  return {
    appName: getFirstWordInString(sentence),
    scopes: scopes,
  };
};

const ThirdParty = () => {
  const [appNameAndScopes, setappNameAndScopes] = useState<AppNameAndScopes[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isInfoShown, setInfoShown] = useState(false);
  const { cidmUrl, accessToken, setAccessToken, isAuthorizeError, languageCode, authorizationCode } =
    useContext(StateContext);
  const isEmptyList = !isLoading && !isError && !appNameAndScopes.length;
  const { stateUSACode = "" } = useContext(StateContext);
  useEffect(() => {
    if (!cidmUrl || isAuthorizeError) {
      return;
    }

    getAccessToken(cidmUrl, authorizationCode)
      .then(tokenResponse => {
        setAccessToken(tokenResponse.data.access_token);
      })
      .catch(() => {
        window.location.assign(window.location.origin + window.location.pathname);
      });
  }, [cidmUrl, setAccessToken]);
  const consentUrl = cidmUrl + consentsUrl;

  const scopeUrl = cidmUrl + scopesDescriptionURL(getStateName(stateUSACode));
  const scopeDescriptionsResponse = useFetch<ScopeHtmlContent[]>(cidmUrl ? scopeUrl : "");

  const applicationNameAndScopeDescriptions = appNameAndScopes.map(element => {
    return {
      applicationName: element.appName,
      scopeDescriptions: scopeDescriptionsResponse.data?.filter(obj => element.scopes.includes(obj.label)) || [],
    };
  });
  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const fetchData = async () => {
      try {
        const res = await axios.get<{ hcscAppConsent: string[] }>(consentUrl, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          withCredentials: true,
        });
        setappNameAndScopes((res.data?.hcscAppConsent || []).map(parseAppNameAndScopes));
      } catch (err) {
        setIsError(true);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [cidmUrl, accessToken]);

  const handleDisconnectAll = () => {
    setappNameAndScopes([]);
    setInfoShown(true);
    setTimeout(() => {
      setInfoShown(false);
    }, 4000);
  };
  const disconnectOneApp = (appName: string) => {
    setappNameAndScopes(appNameAndScopes.filter(element => element.appName !== appName));
  };
  if (isAuthorizeError || isError) {
    return (
      <div className={classNames(styles["right-pane"], styles.errorWrapper)}>
        <ErrorLayout
          header={translations.unableLoadApps[languageCode]}
          subheader={translations.sorryMessage[languageCode]}
        />
      </div>
    );
  }

  if (isLoading || scopeDescriptionsResponse.loading) {
    return null;
  }

  return (
    <div className={styles["right-pane"]}>
      <p className={styles.header}>{translations.connectedApps[languageCode]}</p>
      <div style={{ width: "100%" }}>
        {isInfoShown && (
          <div className={styles.alert}>
            <Alert kind="success" onDismiss={() => setInfoShown(false)}>
              {translations.allAppsDisconected[languageCode]}
            </Alert>
          </div>
        )}
        <h1 className={styles["right-pane__title"]}>{translations.controlWhichApps[languageCode]}</h1>
        <p className={styles["right-pane__paragraph"]}>
          {translations.servicesAccess[languageCode](stateUSACode.toUpperCase())}
        </p>
        <DisconnectButton
          thirdPartyConsents={appNameAndScopes.map(element => element.appName)}
          disabled={isEmptyList}
          onDisconnect={handleDisconnectAll}
          buttonTitle={translations.disconnectAll[languageCode]}
          dialogTitle={translations.disconnectAllApps[languageCode]}
        />
      </div>
      {isEmptyList && <div className={styles.emptyList}>{translations.noConnectedApps[languageCode]}</div>}

      <ThirdPartyAccordion
        applicationNameAndScopeDescriptions={applicationNameAndScopeDescriptions}
        isError={isError}
        isLoading={isLoading}
        disconnect={disconnectOneApp}
      />
    </div>
  );
};

export default ThirdParty;
